import { getAllSets, isContentUnverified } from '@sayfazkari/content';
import { useSession, setStatus } from '../../store/session';
import { useSalawat } from '../../store/salawat';
import { currentSetByClock } from '../../lib/time';
import { currentStreak } from '../../lib/streak';
import { plural } from '../../lib/plural';
import { Credit } from './Credit';
import { ru } from '../../strings/ru';
import styles from './HomeScreen.module.css';

export function HomeScreen() {
  const startSet = useSession((s) => s.startSet);
  const openSettings = useSession((s) => s.openSettings);
  const openSalawat = useSession((s) => s.openSalawat);
  const salawatToday = useSalawat((s) => s.today);
  // Пересчитывается при смене revision — после дочитанного набора
  // и после прихода истории из облака
  const revision = useSession((s) => s.revision);
  const suggested = currentSetByClock();
  const streak = currentStreak();
  void revision;

  return (
    <div className={styles.screen}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>{ru.home.subtitle}</h1>
          {streak > 0 && (
            <p className={styles.streak}>
              <span className={styles.streakNum}>{streak}</span>{' '}
              {plural(
                streak,
                ru.home.streakOne,
                ru.home.streakFew,
                ru.home.streakMany,
              )}
            </p>
          )}
        </div>
        <button
          className={styles.settingsBtn}
          onClick={openSettings}
          aria-label={ru.settings.title}
        >
          <SettingsIcon />
        </button>
      </header>

      {isContentUnverified && (
        <p className={styles.warning} role="status">
          {ru.unverifiedWarning}
        </p>
      )}

      <ul className={styles.sets}>
        {getAllSets().map((set) => {
          const total = set.items.reduce((n, z) => n + z.repeat, 0);
          const status = setStatus(set.id);
          return (
            <li key={set.id}>
              <button
                className={styles.setCard}
                data-suggested={set.id === suggested && status !== 'done'}
                data-status={status}
                onClick={() => startSet(set.id)}
              >
                <span className={styles.setIcon} aria-hidden="true">
                  {set.id === 'morning' ? <SunIcon /> : <MoonIcon />}
                </span>
                <span className={styles.setText}>
                  <span className={styles.setTitle}>{set.titleRu}</span>
                  {set.subtitleRu && (
                    <span className={styles.setSubtitle}>{set.subtitleRu}</span>
                  )}
                  <span className={styles.setMeta}>
                    {status === 'done'
                      ? ru.home.done
                      : status === 'inProgress'
                        ? ru.home.resume
                        : `${set.items.length} азкаров · ${total} повторений`}
                  </span>
                </span>
                <span className={styles.chevron} aria-hidden="true">
                  {status === 'done' ? '✓' : '›'}
                </span>
              </button>
            </li>
          );
        })}

        {/* Салават стоит отдельно от наборов: у него нет порядка,
            кратности и завершения — его читают сколько получится */}
        <li>
          <button className={styles.setCard} onClick={openSalawat}>
            <span className={styles.setIcon} aria-hidden="true">
              <TasbihIcon />
            </span>
            <span className={styles.setText}>
              <span className={styles.setTitle}>{ru.salawat.title}</span>
              <span className={styles.setSubtitle}>
                {ru.salawat.cardSubtitle}
              </span>
              {salawatToday > 0 && (
                <span className={styles.setMeta}>
                  {ru.salawat.today} · {salawatToday}
                </span>
              )}
            </span>
            <span className={styles.chevron} aria-hidden="true">
              ›
            </span>
          </button>
        </li>
      </ul>

      <Credit />
    </div>
  );
}

/** Чётки: разрыв внизу и разделительная бусина — как у настоящей мисбахи */
function TasbihIcon() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true">
      <circle cx="7.87" cy="17.9" r="1.55" />
      <circle cx="5.53" cy="15.16" r="1.55" />
      <circle cx="4.81" cy="11.62" r="1.55" />
      <circle cx="5.89" cy="8.18" r="1.55" />
      <circle cx="8.51" cy="5.7" r="1.55" />
      <circle cx="12" cy="4.8" r="1.55" />
      <circle cx="15.49" cy="5.7" r="1.55" />
      <circle cx="18.11" cy="8.18" r="1.55" />
      <circle cx="19.19" cy="11.62" r="1.55" />
      <circle cx="18.47" cy="15.16" r="1.55" />
      <circle cx="16.13" cy="17.9" r="1.55" />
      <circle cx="12" cy="20.3" r="2.15" />
    </svg>
  );
}

function SunIcon() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <circle cx="12" cy="12" r="4.2" />
      <path d="M12 2.4v2.2M12 19.4v2.2M4.2 12H2M22 12h-2.2M6.3 6.3 4.8 4.8M19.2 19.2l-1.5-1.5M17.7 6.3l1.5-1.5M4.8 19.2l1.5-1.5" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.5 14.6A8.6 8.6 0 0 1 9.4 3.5a8.6 8.6 0 1 0 11.1 11.1Z" />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1A1.7 1.7 0 0 0 8.9 19a1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.6 8.4a1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" />
    </svg>
  );
}
